import React, { useEffect, useState } from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Divider, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import SearchBox from '../../common/forms/SearchBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useOfficeFiles } from '../../../hooks/uploads/useOfficeFiles';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import SortBySelect from '../controls/SortBySelect';
import { bytesToMegaBytes } from '../../../utils/bytestoMegaBytes';
import AttachmentView from './AttachmentView';
import FileRenameForm from '../forms/FileRenameForm';
import { triggerDownload } from '../../../utils/triggerDownload';
import { downloadFile } from '../../../api/uploadService';
import { StorageStatus } from '../display/StorageStatus';
import { useDeleteFile } from '../../../hooks/uploads/useDeleteFile';

export default function FileMaintenance({mode = 'list'}) {
    const { t } = useTranslation('common'); 
    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSort(['size', 'desc']);
    };

    const { showSnackbar } = useSnackbar();

    const { items, loading, loadMore, searchFunction, resetParent, reorderList, updateItem, removeItem } = useOfficeFiles(open, 1, 10);

    const [sort, setSort] = useState(['size', 'desc']);

    const handleSortSelectChange = (newSort) => {
      setSort(newSort);
    };

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);

    const [selectedAttachment, setSelectedAttachment] = React.useState(null);

    const handleListItemClick = (attachment) => {
        setSelectedAttachment(attachment);
        setViewOpen(true);
    };

    const handleRenameClick = (attachment) => {
      setSelectedAttachment(attachment);
      setFormOpen(true);
    };

    const onFileRenameSuccess = (updatedAttachment) => {
      setFormOpen(false);
      updateItem(updatedAttachment); // ✅ patch local state
      showSnackbar(t("files.messages.rename_success"), "success");
    };

    const handleDownloadClick = async (attachment) => {
      const fileData = await downloadFile(attachment.id);
      triggerDownload(fileData, attachment.filename);
    };

    const onFileDeleteSuccess = async (attachmentId) => {
      showSnackbar(t('files.messages.delete_success'), "success");
      removeItem(attachmentId);
      await resetParent();
    };

    const { remove } = useDeleteFile(onFileDeleteSuccess);

    const handleDeleteAttachmentClick = async (attachment) => {
      setSelectedAttachment(attachment);
      try {
          await remove(attachment.id);
      } catch (err) {
          showSnackbar(t('files.messages.delete_fail'), "error");
      }
    };

    useEffect(() => {
      resetParent();
    }, [open]);

    useEffect(() => {
      reorderList(1, sort[0], sort[1]);
    }, [sort]);

    return (
        <React.Fragment>
          {mode === 'list' ? 
          <ListItem>
              <ListItemButton onClick={handleClickOpen}>
              <ListItemIcon>
                  <PermMediaIcon fontSize='large' color='secondary'/>
              </ListItemIcon>
              <ListItemText 
                  primary="File Maintenance"
                  secondary="view and manage file attachments"
              />
              </ListItemButton>
          </ListItem>
          : null }
          {mode === 'button' ? 
          <Button onClick={handleClickOpen}>File Maintenance</Button>
          : null }

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            <Stack spacing={1}>
                <Typography variant="h6">File Maintenance</Typography>
                <SearchBox searchFunction={searchFunction} resetParent={resetParent} triggerClearInput={null}/>
                <SortBySelect sort={sort} onChange={handleSortSelectChange} />
                <Divider />
                <StorageStatus triggerUpdate={items} />
                <Divider />
            </Stack>
            </DialogTitle>

            <DialogContent>
                <>
                <List sx={{ minHeight: 500 }}>
                  {items?.data?.map((attachment) => (
                    <ListItem 
                        key={attachment.id}
                        sx={{ py: 0 }}
                        alignItems="flex-start"
                        secondaryAction={
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <VerticalHamburgerMenu
                                options={[
                                    { 
                                        id: 1, 
                                        name: 'View',
                                        callback: () => handleListItemClick(attachment)
                                    },
                                    { 
                                        id: 2, 
                                        name: 'Download',
                                        callback: () => handleDownloadClick(attachment)
                                    },
                                    { 
                                        id: 3, 
                                        name: 'Rename',
                                        callback: () => handleRenameClick(attachment)
                                    },
                                    {
                                        id: 4,
                                        name: 'Delete',
                                        callback: () => handleDeleteAttachmentClick(attachment),
                                        confirmMessage: t('files.messages.delete_confirm', {name: attachment.filename})
                                    }
                                ]}
                                />
                            </Box>
                        }
                    >
                        <ListItemButton sx={{ py: 0 }} onClick={() => {
                          handleListItemClick(attachment);
                        }}>
                        <ListItemIcon>
                            {attachment.mimetype.includes('image/') ? ( 
                                <ImageIcon fontSize="large" color="primary" /> 
                            ) : attachment.mimetype.includes('audio/') ? ( 
                                <AudioFileIcon fontSize="large" color="primary" /> 
                            ) : attachment.mimetype.includes('video/') ? ( 
                                <VideoFileIcon fontSize="large" color="primary" /> 
                            ) : attachment.mimetype.includes('application/pdf') ? ( 
                                <PictureAsPdfIcon fontSize="large" color="primary" /> 
                            ) : ( <InsertDriveFileIcon fontSize="large" color="primary" /> 

                            )}
                        </ListItemIcon>
                        <ListItemText
                        primary={
                          <>
                            <Typography noWrap={true} variant="body2" sx={{ lineHeight: 1, margin: 0 }}>
                                {attachment.filename}
                            </Typography>
                            <Typography noWrap={true} variant="body2" sx={{ lineHeight: 1, margin: 0 }}>
                                {bytesToMegaBytes(attachment.size)} MB
                            </Typography>
                            { attachment.pet ?
                            <Typography noWrap={true} variant="body2"  sx={{ lineHeight: 1, margin: 0, color: "gray" }}>
                                {`${attachment.pet?.name} (${attachment.pet?.owner?.last_name}, ${attachment.pet?.owner?.first_name})`}
                            </Typography>
                            : null }
                          </>
                        }
                        secondary={formatToLocalDateTime(attachment.createdAt)}
                        />
                        </ListItemButton>
                    </ListItem>
                  ))}
                </List>

                {/* Load More only shows if more pages exist */}
                {items?.pagination?.page < items?.pagination?.totalPages && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <IconButton 
                            onClick={loadMore}
                            sx={{
                                width: '100%',              // fill container width
                                justifyContent: 'center',   // center the icon horizontally
                                color: 'primary.main',      // use theme's primary color
                            }}
                        >
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Box>
                )}
                </>
            </DialogContent>
            <DialogActions>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>

        <AttachmentView 
          attachment={selectedAttachment}
          open={viewOpen}
          onClose={() => setViewOpen(false)}
        />

        <FileRenameForm 
          successCallback={onFileRenameSuccess}
          attachment={selectedAttachment}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
          }}
        />
        
        </React.Fragment>
    );
}